import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useUserChallenges } from '../UserChallengeProvider'
import { useGuest } from '@/features/guest/GuestProvider'
import { Button } from '@/components/Button'
import { ChallengeCard } from '@/components/ChallengeCard'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { SimpleNotificationBanner } from '@/components/SimpleNotificationBanner'
import { NotificationSetupBanner } from '@/components/NotificationSetupBanner'
// GuestNotificationSetup component removed - Firebase messaging no longer used
import { useConfirm } from '@/hooks/useConfirm'
import { getChallengesForAutoResume, rescheduleAllNotifications } from '@/utils/simpleChallengeUtils'
import { PauseDuration } from '@/components/PauseDurationModal'
// Guest notification setup removed - Firebase messaging no longer used

export function HomePage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { challengeRepository, isGuest } = useUserChallenges()
  const { getChallengeCount } = useGuest()
  const queryClient = useQueryClient()
  const { confirmState, showConfirm } = useConfirm()

  const { data: challenges = [], isLoading } = useQuery({
    queryKey: ['challenges', user?.uid],
    queryFn: async () => {
      console.log('Fetching challenges for user:', user?.uid, 'isGuest:', isGuest)
      const allChallenges = await challengeRepository.listByOwner(user?.uid)
      console.log('Found challenges:', allChallenges.length, allChallenges)
      
      // Sort challenges: active first, then by frequency
      return allChallenges.sort((a, b) => {
        // First, sort by active status (active challenges first)
        if (a.status === 'active' && b.status !== 'active') return -1
        if (a.status !== 'active' && b.status === 'active') return 1
        
        // Then sort by frequency priority
        const frequencyPriority = {
          'hourly': 0,
          'daily': 1,
          'weekly': 2,
          'monthly': 3,
          'custom': 4
        }
        
        const aFrequency = a.frequency.mode
        const bFrequency = b.frequency.mode
        
        return (frequencyPriority[aFrequency] || 999) - (frequencyPriority[bFrequency] || 999)
      })
    },
  })

  const pauseChallenge = useMutation({
    mutationFn: ({ id, duration }: { id: string; duration?: PauseDuration }) => {
      if (duration) {
        // Handle different pause durations
        switch (duration.type) {
          case 'days':
            const pauseUntil = new Date()
            pauseUntil.setDate(pauseUntil.getDate() + duration.value)
            return challengeRepository.pauseWithDuration(id, pauseUntil.toISOString())
          case 'until':
            return challengeRepository.pauseWithDuration(id, duration.value)
          default:
            return challengeRepository.pause(id)
        }
      } else {
        return challengeRepository.pause(id)
      }
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] })
      toast.success(t('challenges.challengePaused'))
      
      // Reschedule notifications since one challenge was paused
      try {
        const updatedChallenges = await challengeRepository.listByOwner(user?.uid)
        await rescheduleAllNotifications(updatedChallenges)
      } catch (error) {
        console.error('Failed to reschedule notifications after pause:', error)
      }
    },
    onError: () => toast.error(t('errors.failedToPauseChallenge')),
  })

  const resumeChallenge = useMutation({
    mutationFn: (id: string) => challengeRepository.resume(id),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] })
      toast.success(t('challenges.challengeResumed'))
      
      // Reschedule notifications since one challenge was resumed
      try {
        const updatedChallenges = await challengeRepository.listByOwner(user?.uid)
        await rescheduleAllNotifications(updatedChallenges)
      } catch (error) {
        console.error('Failed to reschedule notifications after resume:', error)
      }
    },
    onError: () => toast.error(t('errors.failedToResumeChallenge')),
  })

  const deleteChallenge = useMutation({
    mutationFn: (id: string) => challengeRepository.delete(id),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['challenges'] })
      toast.success(t('challenges.challengeDeleted'))
      
      // Reschedule notifications since one challenge was deleted
      try {
        const updatedChallenges = await challengeRepository.listByOwner(user?.uid)
        await rescheduleAllNotifications(updatedChallenges)
      } catch (error) {
        console.error('Failed to reschedule notifications after deletion:', error)
      }
    },
    onError: () => toast.error(t('errors.failedToDeleteChallenge')),
  })

  const hasChallenges = challenges.length > 0
  const challengeCount = getChallengeCount()
  
  console.log('HomePage state:', {
    user: user?.uid,
    isGuest,
    challenges: challenges.length,
    hasChallenges,
    challengeCount,
    isLoading
  })

  // Auto-resume expired challenges and reschedule notifications on app startup
  useEffect(() => {
    if (!challenges.length) return

    const challengesToResume = getChallengesForAutoResume(challenges)
    
    if (challengesToResume.length > 0) {
      // Resume all expired challenges
      challengesToResume.forEach(challenge => {
        resumeChallenge.mutate(challenge.id)
      })
      
      toast.success(t('challenges.challengesAutomaticallyResumed', { count: challengesToResume.length }))
    }

    // Reschedule notifications on app startup to ensure they're properly set
    const initializeNotifications = async () => {
      try {
        // Check if we have any active challenges to schedule
        const activeChallenges = challenges.filter(c => c.status === 'active')
        if (activeChallenges.length === 0) {
          console.log('No active challenges to schedule notifications for')
          return
        }

        // Check notification permission and enabled status
        const permission = Notification.permission
        const notificationsEnabled = localStorage.getItem('notifications-enabled') !== 'false'
        
        console.log('Notification check:', {
          permission,
          notificationsEnabled,
          activeChallenges: activeChallenges.length,
          totalChallenges: challenges.length
        })
        
        if (permission === 'granted' && notificationsEnabled) {
          console.log('Rescheduling notifications for', activeChallenges.length, 'active challenges on app startup')
          await rescheduleAllNotifications(activeChallenges)
        } else {
          console.log('Notifications not scheduled:', permission !== 'granted' ? 'No permission' : 'Notifications disabled')
        }
      } catch (error) {
        console.error('Failed to reschedule notifications on startup:', error)
      }
    }

    initializeNotifications()
  }, [challenges])

  return (
    <>
      {hasChallenges ? (
        <div className="min-h-screen bg-bg">
          <NotificationSetupBanner />
          <div className="max-w-4xl mx-auto px-4 sm:px-lg py-4 sm:py-2xl">
            <SimpleNotificationBanner />
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 sm:mb-2xl gap-4 sm:gap-0">
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-text mb-2 sm:mb-md">{t('challenges.yourChallenges')}</h1>
                <p className="text-sm sm:text-base text-text-muted leading-relaxed">
                  {isGuest && (
                    <span>
                      {t('challenges.challengesSavedLocally', { count: challengeCount })} 
                      <Link to="/register" className="text-primary hover:text-primary-hover underline decoration-2 underline-offset-2 ml-xs font-medium transition-colors">
                        {t('auth.signUp')}
                      </Link> {t('challenges.signUpToSync', { signUp: '' }).replace(/^[^}]*}/, '').trim()}
                    </span>
                  )}
                  {!isGuest && (
                    <span>{t('challenges.stayConsistentDescription')}</span>
                  )}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:ml-xl">
                <Link to="/challenges/new/selection" className="w-full sm:w-auto">
                  <Button className="shadow-sm font-medium w-full sm:w-auto">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('challenges.createChallenge')}
                  </Button>
                </Link>
              </div>
            </div>

            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-surface rounded-xl border border-border shadow-sm p-6 animate-pulse">
                    <div className="h-6 bg-bg-alt rounded w-1/3 mb-3"></div>
                    <div className="h-4 bg-bg-alt rounded w-1/2 mb-4"></div>
                    <div className="flex gap-2">
                      <div className="h-8 bg-bg-alt rounded w-16"></div>
                      <div className="h-8 bg-bg-alt rounded w-16"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {challenges.map((challenge) => (
                  <div key={challenge.id} className="bg-surface rounded-xl border border-border shadow-sm overflow-hidden">
                    <ChallengeCard
                      challenge={challenge}
                      onPause={(id, duration) => pauseChallenge.mutate({ id, duration })}
                      onResume={resumeChallenge.mutate}
                      onDelete={deleteChallenge.mutate}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="min-h-screen bg-bg">
          <div className="max-w-7xl mx-auto px-4 sm:px-lg py-8 sm:py-3xl">
            <div className="text-center">
              <h1 className="text-2xl sm:text-4xl font-semibold text-text mb-4 sm:mb-lg">
                {t('common.appTagline')}
              </h1>
              <p className="text-lg sm:text-xl text-text-muted mb-8 sm:mb-2xl max-w-3xl mx-auto px-4">
                {t('challenges.buildLastingHabits')}
              </p>
              
              <div className="mb-8 sm:mb-3xl">
                <Link to="/challenges/new/selection">
                  <Button size="lg" className="w-full sm:w-auto">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    {t('challenges.createYourFirstChallenge')}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        confirmVariant={confirmState.confirmVariant}
        onConfirm={confirmState.onConfirm || (() => {})}
        onCancel={confirmState.onCancel || (() => {})}
      />

    </>
  )
}